import { RegionKeys } from 'src/core/provider/AppMeta';
import { fromJsonArray } from 'src/core/utils';
import ActionDTO from 'src/entities/basic-types/ActionDTO';
import BasicType from 'src/entities/basic-types/BasicType';
import { SCHEMA_ADDRESS_SHAPE, SCHEMA_BASIC_ADDRESS_SHAPE } from 'src/schemas/MyAddressBook';
import CustomerData from '../customer/CustomerData';

export class BasicAddress extends BasicType {
    public readonly id: number;
    public readonly vatNumber?: string;
    public readonly hasValidVat: boolean;

    public readonly addressChangeRequired: boolean;
    public readonly addressChangeReason?: string;

    public readonly addresseeName: string;
    public readonly addresseeName2?: string;

    public readonly street: string;
    public readonly streetNumber: string;
    public readonly postalCode: string;
    public readonly city: string;
    public readonly countryIso2: RegionKeys;

    constructor(json: any) {
        super();

        this.id = json.id;

        this.vatNumber = json.vatNumber ?? '';
        this.hasValidVat = json.hasValidVat;

        this.addressChangeRequired = json.addressChangeRequired;
        this.addressChangeReason = json.addressChangeReason ?? undefined;

        this.addresseeName = json.addresseeName;
        this.addresseeName2 = json.addresseeName2 ?? '';

        this.street = json.street;
        this.streetNumber = json.streetNumber;
        this.postalCode = json.postalCode;
        this.city = json.city;
        this.countryIso2 = json.countryIso2;
    }

    public get partsList(): string[] {
        return [this.namePart, this.addressPart, this.postalPart, this.countryIso2].filter(
            part => part != null
        ) as string[];
    }

    public get namePart(): string {
        return [this.addresseeName, this.addresseeName2].filter(x => x).join(' / ');
    }

    public get addressPart(): string {
        return `${this.street} ${this.streetNumber}`;
    }

    public get postalPart(): string {
        return `${this.postalCode} ${this.city}`;
    }

    public toString(): string {
        return `${this.street} ${this.streetNumber} - ${this.postalCode} ${this.city}`;
    }

    static getFormValues(address?: BasicAddress): Partial<SCHEMA_BASIC_ADDRESS_SHAPE> {
        return {
            type: address?.vatNumber && address?.vatNumber.length > 0 ? 'business' : 'private',
            vatNumber: address?.vatNumber ?? '',
            addressChangeRequired: address?.addressChangeRequired ?? false,
            addressChangeReason: address?.addressChangeReason ?? '',
            addresseeName: address?.addresseeName ?? '',
            addresseeName2: address?.addresseeName2 ?? '',
            street: address?.street ?? '',
            streetNumber: address?.streetNumber ?? '',
            postalCode: address?.postalCode ?? '',
            city: address?.city ?? '',
            countryIso2: address?.countryIso2,
        };
    }
}

export type AddressState = 'confirmed' | 'unconfirmed' | 'rejected';
export type AddressAction = 'popup_edit';

export type AdditionalInvoiceData = {
    name: string;
    value: string;
};

const DEFAULT_ADDITIONAL_INVOICE_DATA: AdditionalInvoiceData[] = [
    { name: 'Kostenstelle', value: '' },
    { name: 'OIB-Nr', value: '' },
    { name: 'Bestellnr', value: '' },
];

export const ColorMapAddressState: Record<AddressState, ContextColor> = {
    confirmed: 'success',
    unconfirmed: 'warning',
    rejected: 'warning',
};
export class Address extends BasicAddress {
    public readonly isDefaultBillingAddress: boolean;
    public readonly isDefaultDeliveryAddress: boolean;

    public readonly contactFirstName?: string;
    public readonly contactLastName?: string;
    public readonly userPhoneNumberId: string;
    public readonly userPhoneNumber: string;

    public readonly invoiceMailAddressIds: string[];
    public readonly deliveryMailAddressIds: string[];
    public readonly invoiceMailAddresses: string[];
    public readonly deliveryMailAddresses: string[];

    public readonly additionalInvoiceData: AdditionalInvoiceData[];

    public readonly state: AddressState;
    public readonly stateMessage?: string;

    public readonly possibleActionDtoList: ActionDTO<AddressAction>[];

    constructor(json: any) {
        super(json);

        this.isDefaultBillingAddress = json.isDefaultBillingAddress;
        this.isDefaultDeliveryAddress = json.isDefaultDelivery;

        this.contactFirstName = json.contactFirstName;
        this.contactLastName = json.contactLastName;
        this.userPhoneNumberId = json.userPhoneNumberId;
        this.userPhoneNumber = json.userPhoneNumber;

        this.invoiceMailAddressIds = json.invoiceMailAddressIds ?? [];
        this.deliveryMailAddressIds = json.deliveryMailAddressIds ?? [];

        this.invoiceMailAddresses = json.invoiceMailAddresses ?? [];
        this.deliveryMailAddresses = json.deliveryMailAddresses ?? [];

        this.additionalInvoiceData = json.additionalInvoiceData ?? [];

        this.state = json.state;
        this.stateMessage = json.stateMessage;

        this.possibleActionDtoList = fromJsonArray<ActionDTO<AddressAction>>(ActionDTO, json.possibleActionDtoList);
    }

    static getFormValues(address?: Address, customerData?: CustomerData): Partial<SCHEMA_ADDRESS_SHAPE> {
        const additionalInvoiceDataMap = new Map(DEFAULT_ADDITIONAL_INVOICE_DATA.map(data => [data.name, data.value]));
        address?.additionalInvoiceData?.forEach(data => additionalInvoiceDataMap.set(data.name, data.value));

        const additionalInvoiceData = Array.from(additionalInvoiceDataMap.entries()).map(([name, value]) => ({
            name,
            value,
        }));

        const defaultMails = customerData?.accountMailAddressId ? [customerData.accountMailAddressId] : [];

        return {
            ...BasicAddress.getFormValues(address),
            isDefaultBillingAddress: address?.isDefaultBillingAddress ?? false,
            isDefaultDeliveryAddress: address?.isDefaultDeliveryAddress ?? false,
            contactFirstName: address?.contactFirstName ?? customerData?.firstName ?? '',
            contactLastName: address?.contactLastName ?? customerData?.lastName ?? '',
            userPhoneNumberId: address?.userPhoneNumberId ?? customerData?.accountPhoneNumberId ?? '',
            invoiceMailAddressIds: address?.invoiceMailAddressIds ?? (defaultMails as any),
            deliveryMailAddressIds: address?.deliveryMailAddressIds ?? (defaultMails as any),
            additionalInvoiceData,
        };
    }
}
